import React, { useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { KdsModifierStyle } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { stripProperties } from '@oolio-group/client-utils';
import { useSession } from '../../hooks/useSession';
import { useKitchenDisplay } from '../../hooks/useKitchenDisplay';
import SettingsScreenStyles from './Settings.styles';
import ModifierStylingSettingsStyles from './ModifierStylingSettings.styles';
import SectionHeading from './SectionHeading';
import Button from '../../component/Buttons/ButtonText';
import Icon from '../../component/Icon/Icon';
import SetModifierStyleModal, {
  ModifierStyleAction,
} from '../../component/Modals/ModifierStyle/SetModifierStyleModal';
import { modifierSwatchColorStyle } from '../../common/modifierColorStyles';
import { useColors } from '../../hooks/useColors';

// Legacy/persisted records may carry a null `conditions` list or null entries
// (the GraphQL output type is `[ModifierCondition]`), so coerce to a clean
// array before anything maps or reads `.length` on it.
function withSafeConditions(style: KdsModifierStyle): KdsModifierStyle {
  return {
    ...style,
    conditions: (style.conditions || []).filter(Boolean),
  };
}

// Normalize the name and keyword text the same way the server does so the
// locally-cached session data matches what the backend persists.
function normalizeModifierStyle(style: KdsModifierStyle): KdsModifierStyle {
  return {
    ...style,
    name: style.name.trim(),
    conditions: (style.conditions || []).map(condition => ({
      ...condition,
      keyword: (condition.keyword || '').trim(),
    })),
  };
}

type Translate = (
  key: string,
  params?: Record<string, string | number>,
) => string;

function formatStyleSubtitle(
  style: KdsModifierStyle,
  translate: Translate,
): string {
  if (style.matchAll) {
    return translate('kitchenDisplay.modifierStyleModeAllModifiers');
  }
  const keywords = style.conditions
    .map(condition => condition.keyword)
    .filter(Boolean);
  const keywordLabel = translate(
    keywords.length === 1
      ? 'kitchenDisplay.modifierStyleSubtitleKeyword'
      : 'kitchenDisplay.modifierStyleSubtitleKeywords',
    { count: keywords.length },
  );
  return keywords.length
    ? `${keywordLabel} · ${keywords.join(', ')}`
    : keywordLabel;
}

const ModifierStylingSettings: React.FC = () => {
  const styles = SettingsScreenStyles();
  const modifierStyling = ModifierStylingSettingsStyles();
  const { translate } = useTranslation();
  const { colors } = useColors();
  const { session, setSession } = useSession();
  const { updateKitchenDisplay } = useKitchenDisplay({
    storeId: session?.currentStore?.id,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  const modifierStyles = (session?.kitchenDisplay?.modifierStyles || []).map(
    (x: KdsModifierStyle) =>
      withSafeConditions(stripProperties(x, '__typename') as KdsModifierStyle),
  ) as KdsModifierStyle[];

  const sortedStyles = modifierStyles
    .slice()
    .sort((a, b) => a.priority - b.priority);

  const editingStyle = modifierStyles.find(style => style.id === editingId);

  const openCreateModal = () => {
    setEditingId(undefined);
    setModalVisible(true);
  };

  const openEditModal = (id: string) => {
    setEditingId(id);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(undefined);
  };

  const onSave = (rawStyle: KdsModifierStyle) => {
    const style = normalizeModifierStyle(rawStyle);
    const exists = modifierStyles.some(s => s.id === style.id);
    const nextModifierStyles = exists
      ? modifierStyles.map(s => (s.id === style.id ? style : s))
      : [...modifierStyles, style];

    updateKitchenDisplay({
      id: session?.kitchenDisplay?.id || '',
      modifierStyles: nextModifierStyles,
    });
    setSession({
      kitchenDisplay: {
        ...session.kitchenDisplay,
        modifierStyles: nextModifierStyles,
      },
    });
    closeModal();
  };

  const onDelete = () => {
    if (!editingId) return;
    const nextModifierStyles = modifierStyles.filter(
      style => style.id !== editingId,
    );

    updateKitchenDisplay({
      id: session?.kitchenDisplay?.id || '',
      modifierStyles: nextModifierStyles,
    });
    setSession({
      kitchenDisplay: {
        ...session.kitchenDisplay,
        modifierStyles: nextModifierStyles,
      },
    });
    closeModal();
  };

  return (
    <>
      <View style={styles.panelSection}>
        <View style={modifierStyling.headerRow}>
          <View style={modifierStyling.headerText}>
            <SectionHeading
              testID="heading-modifier-styling"
              title={translate('kitchenDisplay.modifierStyling')}
            />
            <Text style={modifierStyling.description}>
              {translate('kitchenDisplay.modifierStylingDescription')}
            </Text>
          </View>
          <View style={modifierStyling.headerButtons}>
            <Button
              testID="btn-add-modifier-style"
              type="positive"
              label={translate('kitchenDisplay.modifierStylingAddStyle')}
              onPress={openCreateModal}
            />
          </View>
        </View>

        {sortedStyles.length === 0 ? (
          <Text
            style={modifierStyling.emptyState}
            testID="modifier-styling-empty-state"
          >
            {translate('kitchenDisplay.modifierStylingEmptyState')}
          </Text>
        ) : (
          sortedStyles.map(style => (
            <TouchableOpacity
              key={style.id}
              testID={`modifier-style-${style.id}`}
              style={modifierStyling.row}
              onPress={() => openEditModal(style.id)}
            >
              <View
                style={[
                  modifierStyling.swatch,
                  modifierSwatchColorStyle(style.styles?.color) ||
                    modifierStyling.swatchEmpty,
                ]}
              />
              <View style={modifierStyling.rowText}>
                <Text style={modifierStyling.rowTitle}>{style.name}</Text>
                <Text style={modifierStyling.rowSubtitle}>
                  {formatStyleSubtitle(style, translate)}
                </Text>
              </View>
              <View style={modifierStyling.rowRight}>
                {style.styles?.italic && (
                  <View style={modifierStyling.italicBadge}>
                    <Text style={modifierStyling.italicBadgeText}>I</Text>
                  </View>
                )}
                <Icon name="angle-right" size={22} color={colors.textLight} />
              </View>
            </TouchableOpacity>
          ))
        )}
      </View>

      <SetModifierStyleModal
        key={`modifier-style-${modalVisible}-${editingId}`}
        visible={modalVisible}
        action={
          editingId ? ModifierStyleAction.EDIT : ModifierStyleAction.CREATE
        }
        existingStyles={modifierStyles}
        style={editingStyle}
        onClose={closeModal}
        onSave={onSave}
        onDelete={onDelete}
      />
    </>
  );
};

export default ModifierStylingSettings;
